import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut as firebaseSignOut, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { csrfService } from '../services/csrfService';
import { clearSessionKey } from '../utils/encryption';
import { useGlobalLoading } from './GlobalLoadingContext';
import {
  getUser,
  updateUserLastLogin,
  createOrUpdateUser
} from '../lib/database';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  account: string | null;
  availableAccounts: string[];
  switchAccount: (account: string) => void;
  signUp: (email: string, password: string, onSuccess?: () => void) => Promise<void>;
  signIn: (email: string, password: string, onSuccess?: () => void) => Promise<void>;
  refreshAccess: () => Promise<void>;
  signOut: () => Promise<void>;
  error: string | null;
  sessionId: string | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [account, setAccount] = useState<string | null>(null);
  const [availableAccounts, setAvailableAccounts] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { resetForAccountSwitch, completeStage, finishLoading } = useGlobalLoading();

  // Return all accounts the email has access to, regardless of Active/Paused status.
  // Why: ex-clients sometimes ask to log in months later to retrieve their data.
  // Access revocation is now an explicit removal from rpc_validate_crm_email's source,
  // not a paused-flag side effect.
  const filterActiveAccounts = (accounts: string[], accountsWithStatus?: Array<{name: string, status: string}>): string[] => {
    if (accountsWithStatus && accountsWithStatus.length > 0) {
      return accountsWithStatus.map(acc => acc.name);
    }
    return accounts;
  };

  const setupUserAccounts = async (email: string, userId: string) => {
    try {
      let retryCount = 0;
      const maxRetries = 3;
      // SECURITY: Sanitize email in logs to prevent information leakage
      const sanitizedEmail = email ? email.substring(0, 3) + '***@' + email.split('@')[1] : '***';
      console.log('Setting up user accounts for:', sanitizedEmail);

      const setupWithRetry = async () => {
        try {
          // SECURITY: Use server-side validation for all users (not a login attempt)
          const serverValidation = await authService.validateUserServerSide(email, false, userId, sessionId);
          console.log('Server validation result:', { valid: serverValidation.valid, accountCount: serverValidation.accounts?.length || 0 });

          if (!serverValidation.valid || !serverValidation.accounts || serverValidation.accounts.length === 0) {
            throw new Error(serverValidation.error || 'No accounts available for user');
          }

          // Filter to only show active accounts
          const allAccounts = serverValidation.accounts;
          const validatedAccounts = filterActiveAccounts(allAccounts, serverValidation.accounts_with_status);
          console.log('Creating/updating user with active accounts count:', validatedAccounts.length);
          await createOrUpdateUser(userId, email, validatedAccounts);
          setAvailableAccounts(validatedAccounts);
          setAccount(validatedAccounts[0]);
          // Security: activeAccount now stored in memory only (not localStorage)
        } catch (error: unknown) {
          if (error.code === 'unavailable' && retryCount < maxRetries) {
            console.log(`Retry attempt ${retryCount + 1} of ${maxRetries}`);
            retryCount++;
            // Exponential backoff: 1s, 2s, 4s
            await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 1000));
            return setupWithRetry();
          }
          throw error;
        }
      };

      await setupWithRetry();
      setLoading(false);

      // Notify GlobalLoadingProvider that authentication is complete
      completeStage('authentication');

    } catch (error) {
      console.error('Error in setupUserAccounts:', error);
      setError('Error setting up user accounts. Please try again.');
      setLoading(false);
      throw error;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          setLoading(true);
          setUser(user);
          const dbUser = await getUser(user.uid);

          if (!dbUser) {
            // New user - setup accounts with server-side validation
            await setupUserAccounts(user.email!, user.uid);
          } else {
            // Existing user - validate access server-side for security (not a login attempt)
            const serverValidation = await authService.validateUserServerSide(user.email!, false, user.uid, sessionId);

            if (!serverValidation.valid) {
              setError(serverValidation.error || 'Access denied');
              await firebaseSignOut(auth);
              setLoading(false);
              return;
            }

            // Update last login and filter to only show active accounts
            await updateUserLastLogin(user.uid);
            const allAccounts = serverValidation.accounts || [];
            const validatedAccounts = filterActiveAccounts(allAccounts, serverValidation.accounts_with_status);

            if (validatedAccounts.length > 0) {
              await createOrUpdateUser(user.uid, user.email!, validatedAccounts);
              setAvailableAccounts(validatedAccounts);

              // Security: No longer restore from localStorage, always use first account
              // Users will need to re-select account on page refresh for enhanced security
              const activeAccount = validatedAccounts[0];

              setAccount(activeAccount);
              // Security: activeAccount now stored in memory only (not localStorage)
            } else {
              setError('No accounts available');
              await firebaseSignOut(auth);
            }
            setLoading(false);

            // Notify GlobalLoadingProvider that authentication is complete
            completeStage('authentication');
          }
        } catch (error) {
          console.error('Error setting up user:', error);
          setLoading(false);
        }
      } else {
        setUser(null);
        setAccount(null);
        setAvailableAccounts([]);
        setSessionId(null);
        // Security: No localStorage cleanup needed (memory-only storage)
        setLoading(false);

        // No user - finish loading to show login page
        finishLoading();
      }
    });

    return () => unsubscribe();
  }, []);

  const switchAccount = async (newAccount: string) => {
    if (availableAccounts.includes(newAccount)) {
      // Check if switching to the same account - no action needed
      if (account === newAccount) {
        console.log(`Already on account ${newAccount} - ignoring switch request`);
        return;
      }

      const oldAccount = account;

      // Trigger unified loading for account switch
      console.log(`Switching from ${oldAccount} to ${newAccount} - starting unified loading`);
      resetForAccountSwitch();

      setAccount(newAccount);
      // Security: activeAccount now stored in memory only (not localStorage)
    }
  };

  const signUp = async (email: string, password: string, onSuccess?: () => void) => {
    try {
      setError(null);
      setLoading(true);

      // SECURITY: First validate user server-side with rate limiting (this is a login attempt)
      const serverValidation = await authService.validateUserServerSide(email, true);
      if (!serverValidation.valid) {
        setError(serverValidation.error || 'Access denied');
        setLoading(false);
        return;
      }

      const userCredential = await createUserWithEmailAndPassword(auth, email, password);

      // Filter to only show active accounts
      const allAccounts = serverValidation.accounts || [];
      const validatedAccounts = filterActiveAccounts(allAccounts, serverValidation.accounts_with_status);

      // Create or update user with account
      await createOrUpdateUser(userCredential.user.uid, email, validatedAccounts);

      setAvailableAccounts(validatedAccounts);
      setAccount(validatedAccounts[0]);
      // Security: activeAccount now stored in memory only (not localStorage)

      // Use callback if provided, otherwise fallback to direct navigation
      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/');
      }

    } catch (error: unknown) {
      if (error.code === 'auth/weak-password') {
          setError(
            'La contraseña es demasiado débil. Debe contener al menos:\n' +
            '- 8 caracteres\n' +
            '- Una letra mayúscula\n' +
            '- Una letra minúscula\n' +
            '- Un número'
          );
        } else if (error.code === 'auth/invalid-email') {
          setError('El email proporcionado no es válido.');
        } else if (error.code === 'auth/email-already-in-use') {
          setError('Este email ya está registrado.');
        } else {
          setError(`Error: ${error.message}`);
        }
      console.error('Signup error:', error);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string, onSuccess?: () => void) => {
    try {
      setError(null);
      setLoading(true);

      // SECURITY: First validate user server-side with rate limiting (this is a login attempt)
      const serverValidation = await authService.validateUserServerSide(email, true);
      if (!serverValidation.valid) {
        setError(serverValidation.error || 'Access denied');
        setLoading(false);
        return;
      }

      // If server validation passes, proceed with Firebase authentication
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Filter to only show active accounts
      const allAccounts = serverValidation.accounts || [];
      const validatedAccounts = filterActiveAccounts(allAccounts, serverValidation.accounts_with_status);

      // Update user's accounts if needed
      await createOrUpdateUser(userCredential.user.uid, email, validatedAccounts);
      setAvailableAccounts(validatedAccounts);
      setAccount(validatedAccounts[0]);
      // Security: activeAccount now stored in memory only (not localStorage)

      // Use callback if provided, otherwise fallback to direct navigation
      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/');
      }

    } catch (error: unknown) {
      if (error.code === 'auth/invalid-credential') {
          setError('Email o contraseña incorrectos.');
        } else {
          setError(`Error: ${error.message}`);
        }
      console.error('Signin error:', error);
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);

      // SECURITY: Clear server-side auth tokens
      authService.clearAuthData();

      // SECURITY: Clear CSRF tokens
      csrfService.clearToken();

      // SECURITY: Clear encryption keys and cached data
      clearSessionKey();

      setAccount(null);
      setAvailableAccounts([]);
      setSessionId(null);
      // Security: No localStorage cleanup needed (memory-only storage)
      navigate('/login');
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const refreshAccess = async () => {
    if (!user?.email) return;

    try {
      setLoading(true);
      // SECURITY: Use server-side validation for access refresh (not a login attempt)
      const serverValidation = await authService.validateUserServerSide(user.email, false, user.uid, sessionId);

      if (serverValidation.valid && serverValidation.accounts && serverValidation.accounts.length > 0) {
        // Filter to only show active accounts
        const allAccounts = serverValidation.accounts;
        const validatedAccounts = filterActiveAccounts(allAccounts, serverValidation.accounts_with_status);

        await createOrUpdateUser(user.uid, user.email, validatedAccounts);
        setAvailableAccounts(validatedAccounts);

        // Keep current account if still valid, otherwise switch to first available
        const newAccount = validatedAccounts.includes(account || '')
          ? account
          : validatedAccounts[0];

        setAccount(newAccount);
        // Security: activeAccount now stored in memory only (not localStorage)
      }
    } catch (error) {
      console.error('Error refreshing access:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      account,
      availableAccounts,
      switchAccount,
      refreshAccess,
      signIn,
      signOut,
      signUp,
      error,
      sessionId
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};